import React from "react";
import { Activity, ArrowRight, Clock3, Radio, Signal, Sparkles, Waves } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Seo from "@/components/Seo";
import { getNativePlatform } from "@/lib/runtime";
import "@/styles/native-live-v3.css";

function GhostMatchCard({ title, status, score, meta }) {
  return (
    <div className="rounded-2xl border border-border/60 bg-card/70 p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-base font-semibold">{title}</p>
          <p className="mt-1 text-xs text-muted-foreground">{meta}</p>
        </div>
        <Badge variant="outline">{status}</Badge>
      </div>
      <div className="mt-4 flex items-center justify-between">
        <div className="h-2 w-24 rounded-full bg-muted" />
        <p className="text-2xl font-bold text-muted-foreground">{score}</p>
        <div className="h-2 w-16 rounded-full bg-muted" />
      </div>
      <div className="mt-4 h-2 rounded-full bg-muted/70" />
    </div>
  );
}

function NativeLivePlaceholder() {
  return (
    <main className="native-v3-live-page">
      <header className="native-v3-live-heading">
        <span className="native-v3-live-kicker"><Radio aria-hidden="true" /> Matchcenter</span>
        <h1 className="native-v3-display">Live</h1>
        <p>Följ matchens utveckling heat för heat.</p>
      </header>

      <section className="native-v3-live-preview" aria-label="Förhandsvisning av kommande livefunktion">
        <div className="native-v3-live-preview-lines" aria-hidden="true" />
        <div className="native-v3-live-preview-topline">
          <span><i /> Livecenter</span>
          <small>Förhandsvisning</small>
        </div>

        <div className="native-v3-live-score-preview" aria-hidden="true">
          <div><span>H</span><strong>32</strong></div>
          <div className="native-v3-live-pulse"><i /><i /><i /></div>
          <div><strong>28</strong><span>B</span></div>
        </div>

        <div className="native-v3-live-heat-track" aria-hidden="true">
          {Array.from({ length: 15 }, (_, index) => <i key={index} data-complete={index < 9 ? "true" : "false"} />)}
        </div>
        <div className="native-v3-live-heat-meta">
          <span>Heat 10 av 15</span>
          <span><Clock3 aria-hidden="true" /> Uppdateras löpande</span>
        </div>
      </section>

      <section className="native-v3-live-message">
        <span className="native-v3-live-message-icon"><Waves aria-hidden="true" /></span>
        <div>
          <small>Arbete pågår</small>
          <h2 className="native-v3-display">Liveupplevelsen byggs just nu</h2>
          <p>Här kommer pågående matcher, aktuell ställning och senaste heat att samlas i en snabb och tydlig vy.</p>
        </div>
      </section>

      <section className="native-v3-live-roadmap" aria-label="Planerade livefunktioner">
        <div><Activity aria-hidden="true" /><span><strong>Matchstatus</strong><small>Poäng och heatprogress</small></span><ArrowRight aria-hidden="true" /></div>
        <div><Sparkles aria-hidden="true" /><span><strong>Senaste händelsen</strong><small>Följ vad som händer nu</small></span><ArrowRight aria-hidden="true" /></div>
        <div><Radio aria-hidden="true" /><span><strong>Samlad livevy</strong><small>Alla aktuella matcher</small></span><ArrowRight aria-hidden="true" /></div>
      </section>

      <p className="native-v3-live-footnote">Live är pausad medan funktionen färdigställs.</p>
    </main>
  );
}

export default function LivePage() {
  if (getNativePlatform() === "android") {
    return (
      <>
        <Seo
          title="Live"
          description="Lapstr Live samlar pågående speedwaymatcher, aktuell ställning och heatprogress."
          canonicalPath="/live"
        />
        <NativeLivePlaceholder />
      </>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-[2rem] border border-border/70 bg-gradient-to-br from-background via-card to-primary/5 p-4 sm:p-6">
      <Seo
        title="Live"
        description="Live-sektionen i Lapstr samlar pågående och aktuella speedwaymatcher. Fler livefunktioner byggs ut löpande."
        canonicalPath="/live"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none select-none space-y-4 blur-[8px] saturate-50 opacity-80"
      >
        <Card className="overflow-hidden border-primary/20 bg-gradient-to-br from-card via-card to-primary/5">
          <CardHeader className="gap-3">
            <div className="flex items-center gap-2 text-primary">
              <Radio className="h-5 w-5" />
              <span className="text-sm font-semibold uppercase tracking-[0.2em]">Live</span>
            </div>
            <CardTitle className="text-3xl">Matchcenter för pågående och aktuella matcher</CardTitle>
            <CardDescription className="max-w-3xl text-sm sm:text-base">
              Live-sidan är förberedd för pågående matcher, heatprogress, senaste heat och separat livevy per match.
            </CardDescription>
          </CardHeader>
        </Card>

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          <GhostMatchCard
            title="Dackarna vs Lejonen"
            status="Live"
            score="31 - 23"
            meta="Tis 19:00 • Målilla Motorstadion"
          />
          <GhostMatchCard
            title="Smederna vs Piraterna"
            status="Kommande"
            score="–"
            meta="Tis 19:00 • Eskilstuna Motorstadion"
          />
          <GhostMatchCard
            title="Västervik vs Indianerna"
            status="Avslutad"
            score="46 - 44"
            meta="Mån 19:00 • Hejla Arena"
          />
        </div>
      </div>

      <div className="absolute inset-0 z-10 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg border-primary/30 bg-background/95 shadow-2xl backdrop-blur">
          <CardHeader className="text-center">
            <div className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Signal className="h-7 w-7" />
            </div>
            <CardTitle>Live-sektionen är under uppbyggnad</CardTitle>
            <CardDescription className="text-sm sm:text-base">
              Sidan är planerad för liveflöde för matcher i Bauhausligan.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-center">
            <div className="flex flex-wrap items-center justify-center gap-2">
              <Badge variant="outline" className="gap-1">
                <Activity className="h-3.5 w-3.5" />
                Liveöversikt
              </Badge>
              {/* <Badge variant="outline" className="gap-1">
                <Waves className="h-3.5 w-3.5" />
                Heatprogress
              </Badge>
              <Badge variant="outline" className="gap-1">
                <Radio className="h-3.5 w-3.5" />
                Egen livevy
              </Badge> */}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
